/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type ValidationErrors } from '@angular/forms';
import { type FormControl, CAPACITY_UNIT, I18nService, TimeService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';

import { healthStatusE } from '../../../../../common/constants';
import { ToolService } from '../../../../../common/services';

// 回滚速率
export enum RollbackSpeedE {
    Low = '1',  // 低
    Mdille = '2',   // 中
    High = '3',   // 高
    Asap = '4' // 最高
}

// 回滚状态
export enum RollbackStatusE {
    NoRollback = '0',  // 未回滚
    Rollbacking = '1',   // 回滚中
}

// 快照类型
export enum SnapTypeE {
    UserSnap = '1',  // 用户快照
    HyperCDP = '2',   // CDP
    PrivateSnap = '4',   // 私有快照
    NDMPPrivateSnap = '8' // NDMP私有快照
}

/**
 * 最小保护周期单位枚举
 */
export enum protectPeriodUnitE {
    day = '46',
    month = '47',
    year = '48'
}

/**
 * 文件最小保护周期相关常量
 */
export enum protectPeriodConstE {
    min = '1',
    maxYear = '20',
    maxMonth = '240',
    maxDay = '7300',
    defaultMonth = '12',
    defaultYear = '20',
    defaultDay = '1'
}


@Injectable({ providedIn: 'any' })
export class FilesystemSnapshotStrService {
    get = this.i18nService.get;

    buttonI18n = {
        createBtn: this.get('common.create_button'),
        deleteBtn: this.get('common.delete_button'),
        modifyBtn: this.get('common.modify_button'),
        rollbackBtn: this.get('common.rollback_button1'),
        modifyRollbackBtn: this.get('protection.fileSystemSnapshot_task_modifyRollbackSpeed_btn'),
        okBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),
        closeBtn: this.get('common.close_button'),
    };

    i18n = {
        protectTypeLabel: this.get('protection.protection_simplifiedMode_selectType_title'),
        addScheduleLabel: this.get('storage.lun_simplifiedMode_hypercdpSnap_tab'),
        singleSnapLabel: this.get('storage.lun_simplifiedMode_Snap_tab'),
        addScheduleCardTip: this.get('protection.protection_simplifiedMode_snapPolicy_info'),
        singleSnapCardTip: this.get('protection.protection_simplifiedMode_OneOffSnap_info'),
        selectScheduleLabel: this.get('protection.protection_simplifiedMode_selectSnapPolicy_title'),
        addNewScheduleBtn: this.get('service.share_simplifiedMode_create_addHypercdp_btn'),

        removeMemberFS: this.get('rest.removeFS_fromHyperCDPSchedule_msg'),
        activateCdpScheduleMsg: this.get('rest.startHyperCDPSchedule_msg'),
        advancedSettingTitle: this.get('service.share_simplifiedMode_create_advance_btn'),
        scheduleLabel: this.get('storage.lun_task_addToHyperCDPScheduley_HyperCDPSchedule_label'),
        createScheduleName: (name: string): string => this.get('rest.create_hyperCDPSchedule_msg', [name]),
        addFsResultName: (fsName: string, scheduleName: string): string =>
            this.get('rest.associateFS_toHyperCDPSchedule_msg', [fsName, scheduleName]),

        ...this.buttonI18n,
        name: this.get('common.name_column'),
        id: this.get('common.ID_column'),
        vStoreNameLab: this.get('vstore_name_lab'),
        vStoreIdLab: this.get('vstore_id_lab'),
        capacityLab: this.get('common.capacity_column'),
        sourceFS: this.get('common.sourceFS_label'),
        avaiableFsLab: this.get('protection.HyperCDPSchedule_task_create_step1_availFS_lab'),
        selectedLab: this.get('protection.HyperCDPSchedule_task_create_step1_selectedFS_lab'),
        emptyErr: this.get('settings.userAndRole_roleManagement_task_add_noSelectObject_tip'),
        filesystem: this.get('common.fileSystem_title'),
        filesystemLabel: this.get('monitor.charts_term_objectType_fileSystem_label'),
        fileSystemName: this.get('service.quota_term_FSName_lab'),
        descriptionLab: this.get('common.description_column'),
        snapshotTag: this.get('protection.replicaiton_task_createAsync_userSnapSync_Tag_lab'),
        snapshotTagTip: this.get('protection.replicaiton_task_createAsync_userSnapSync_Tag_tip'),
        healthStatus: this.get('common.healthStatus_column'),
        sourceFilesystemName: this.get('protection.fsSnap_term_view_sourceFilesystemName_column'),
        sourceFilesystemID: this.get('protection.fsSnap_term_view_sourceFilesystemID_column'),
        snapshotUsedSpace: this.get('protection.fsSnap_term_view_snapUsedSpace_column'),
        snapshotSSDSpace: this.get('protection.fsSnap_task_create_snapUsedSSDSpace_label'),
        snapshotTotalSpace: this.get('protection.fsSnap_task_create_snapSpace_label'),
        createTime: this.get('common.createTime_label'),
        rollbackTitle: this.get('protection.snapshotCG_term_startRollbackBtn_rollbackSnap_title'),
        rollbackCdpTitle: this.get('protection.HyperCDP_term_fs_rollback_title'),
        rollbackSpeed: this.get('protection.snapshot_task_detail_rollbackSpeed_lab'),
        rollbackSpeedMsg: this.get('protection.fileSystemSnapshot_task_modifyRollbackSpeed_confirm_msg'),
        rollbackSpeedTip: this.get('protection.fileSystemSnapshot_task_rollbackSpeed_valid_label'),
        modifyRollbackSpeedTip: this.get('protection.fileSystemSnapshot_task_modifyRollbackSpeed_error_tip'),
        modifyRollbackSpeedCDPTip: this.get('protection.HyperCDP_task_fs_modifyRollbackSpeed_error_tip'),
        rollbackStartTime: this.get('protection.snapshot_task_detail_startTimeOfRollback_lab'),
        rollbackEndTime: this.get('protection.snapshot_task_detail_endTimeOfRollback_lab'),
        rollbackProgress: this.get('protection.fsSnap_term_view_rollbackProgress_column'),
        isScheduled: this.get('protection.HyperCDP_term_timing_column'),
        yesTip: this.get('common.term_yes_label'),
        noTip: this.get('common.term_No_label'),
        typeLab: this.get('common.type_column'),
        vStoreLab: this.get('common.owningVstore_lab'),
        leftSearchPlaceholder: this.get('service.quota_term_FSName_lab'),

        createTitle: this.get('protection.protectionGroup_createSnapshot_button'),
        modifyTitle: this.get('protection.protectionGroup_modifySnapshot_title'),
        deleteTitle: this.get('protection.snapshot_task_delete_title'),
        createCdpTitle: this.get('protection.HyperCDP_task_create_title'),
        modifyCdpTitle: this.get('protection.HyperCDP_task_modify_title'),
        deleteCdpTitle: this.get('protection.HyperCDP_task_delete_title'),
        deleteMsg: this.get('protection.fsSnap_task_delete_info_msg'),
        deleteMsgForAntiRansomware: this.get('protection.filesystem_snapshot_task_delete_info_msg'),

        operationBtn: this.get('common.operation_button'),
        userSnapSyncTagValid: this.get('protection.replicaiton_task_createAsync_userSnapSync_Tag_valid'),
        wormTab: this.get('protection.HyperCDPSchedule_task_create_step2_secureSnap_lab'),
        protectTime: this.get('service.filesystem_task_create_worm_para_protectTime_value_min_label'),
        protectPeriodRangeLabel: (min, max): string => this.get('common.valueRange_label', [min, max]),
        enableAutoDelete: this.get('service.filesystem_task_create_worm_para_enableAutoDelete_checkbox'),
        enableLab: this.get('common.enable_label'),
        autoDeleteInfo: this.get('protection.HyperCDPSchedule_task_create_step2_autoDelete_info'),
        autoDeleteTab: this.get('service.filesystem_task_create_worm_para_enableAutoDelete_checkbox'),
        valueSpace: (value): string => this.get('common.unit_value', [value]),
        protectTimeValue: (value, options, unit): string => this.i18n.valueSpace(value) + this.util.getLabelFromOptions(options, unit),
        isExpiredCol: this.get('protection.HyperCDPSchedule_task_view_exceedProtectDays_col'),
        leftDaysCol: this.get('protection.HyperCDPSchedule_task_view_remainDays_col'),
        createFsSnapHighRisk: this.get('protection.dataProtection_term_FS_createSecureSnap_msg'),
        modifyFsSnapHighRisk: (value): string => this.get('protection.dataProtection_term_FS_modifySnapSecureProperty_msg', [value]),
        enableFsSnapHighRisk: this.get('protection.dataProtection_term_FS_enableSecureSnap_msg'),

        snapshotNameRuleTip: this.get('protection.snapshot_task_create_defaultNameRule_tip'),
        cdpNameRuleTip: this.get('protection.HyperCDP_task_create_defaultNameRule_tip'),
    };

    /**
     * 最小保护期
     */
    protectPeriodOptions: SelectOption[] = [
        {
            label: this.get('common.lower_days_label'),
            value: protectPeriodUnitE.day,
        },
        {
            label: this.get('common.time_months_label'),
            value: protectPeriodUnitE.month,
        },
        {
            label: this.get('common.time_years_label'),
            value: protectPeriodUnitE.year,
        },
    ];

    /**
     * 健康状态选项
     */
    healthStatusOpts: SelectOption[] = this.tool.getKeyValeOptions(healthStatusE, [
        healthStatusE.Normal.value,
        healthStatusE.Fault.value,
    ]);

    // 回滚速率选项列表
    rollbackSpeedOptions: SelectOption[] = [
        {
            label: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_LOW'),
            value: RollbackSpeedE.Low,
        },
        {
            label: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_MIDDLE'),
            value: RollbackSpeedE.Mdille,
        },
        {
            label: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_HIGH'),
            value: RollbackSpeedE.High,
        },
        {
            label: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_ASAP'),
            value: RollbackSpeedE.Asap,
        },
    ];

    snapTypeOpt: SelectOption[] = [
        {
            label: this.get('protection.snapshot_task_fs_view_type_UserSnap_value'),
            value: SnapTypeE.UserSnap,
        },
        {
            label: this.get('protection.snapshot_task_fs_view_type_HyperCDP_value'),
            value: SnapTypeE.HyperCDP,
        },
        {
            label: this.get('protection.snapshot_task_fs_view_type_privSnap_value'),
            value: SnapTypeE.PrivateSnap,
        },
        {
            label: this.get('protection.snapshot_task_fs_view_type_privSnap_value'),
            value: SnapTypeE.NDMPPrivateSnap,
        },
    ];

    yesNoOpt: SelectOption[] = [
        {
            label: this.get('common.term_yes_label'),
            value: '2',
        },
        {
            label: this.get('common.term_No_label'),
            value: '1',
        },
    ];

    /**
     * 安全快照、自动删除是否启用
     */
    wormOrDelOptions: SelectOption[] = [
        {
            label: this.get('common.enabled_switch_column'),
            value: 'true',
        },
        {
            label: this.get('common.disabled_switch_column'),
            value: 'false',
        },
    ];

    /**
     * 是否过保护期
     */
    isExpiredOptions: SelectOption[] = [
        {
            label: this.i18n.yesTip,
            value: 'true',
        },
        {
            label: this.i18n.noTip,
            value: 'false',
        },
    ];

    constructor(
        private i18nService: I18nService,
        public tool: ToolService,
        public util: UtilService,
        public time: TimeService,
    ) { }

    snapTypeRender = (value: string): string => {
        const result = this.snapTypeOpt.find((a) => Number(a.value) === Number(value));
        return result && result.label || UNPLACEHOLDER;
    };

    yesNoRender = (value: string): string => {
        const result = this.yesNoOpt.find((a) => Number(a.value) === Number(value));
        return result && result.label || UNPLACEHOLDER;
    };

    rollbackSpeedRender = (value: string): string => {
        const result = this.rollbackSpeedOptions.find((a) => Number(a.value) === Number(value));
        return result && result.label || UNPLACEHOLDER;
    };

    timeRender = (value: string): string => value && !this.tool.isInvalidNum(value, true) ?
        this.time.formatUTC(Number(value) * 1e3) :
        UNPLACEHOLDER;

    /**
     * 回滚时间
     */
    rollbackTimeRender = (value: string): string => Number(value) !== 0 ? this.timeRender(value) : UNPLACEHOLDER;

    spaceRender = (value: string): string => {
        if (!value) {
            return UNPLACEHOLDER;
        }
        return this.tool.adaptUnit2(value, CAPACITY_UNIT.BYTE).label;
    };

    requiredErr(name: string): string {
        return this.get('common.null_validation', [name]);
    }

    rangeByteLengthErr(min: number, max: number): string {
        return this.get('common.length_msg', [min, max]);
    }

    strictNameErr(name: string): string {
        return this.get('service.filesystem_term_name_rule', [name]);
    }

    snapshotNameErr(name: string): string {
        return this.get('protection.snapshot_task_create_FS_name_validation', [name]);
    }

    createFsResultName(name: string, isCdp?: boolean): string {
        return this.get(isCdp ? 'rest.create_HyperCDP_result_msg' : 'rest.createFSsnapshot_msg', [name]);
    }

    modifyFsResultName(name: string, isCdp?: boolean): string {
        return this.get(isCdp ? 'rest.modify_HyperCDP_result_msg' : 'rest.modifyFSsnapshot_msg', [name]);
    }

    deleteFsResultName(name: string, isCdp?: boolean): string {
        return this.get(isCdp ? 'rest.delete_HyperCDP_result_msg' : 'rest.deleteFSsnapshot_msg', [name]);
    }

    rollback(name: string, isCdp?: boolean): string {
        return this.get(isCdp ? 'rest.startRollback_HyperCDP_result_msg' : 'rest.rollbackFSsnapshot_msg', [name]);
    }

    modifyRollbackSpeed(name: string): string {
        return this.get('rest.modifyRollbackSpeed_snapshot_msg', [name]);
    }

    getRollbackInfo(): string {
        return this.get('protection.filesystem_snapshot_task_rollbackSwitchOn_info_message');
    }

    snapshotNameValidator(control: FormControl): ValidationErrors | null {
        const reg = /^[a-zA-Z0-9-_]*$/;
        const isValid = reg.test(control.value);
        return isValid ? null : { snapshotName: true };
    }

    /**
     * 标签设置
     */
    snapTagValidator(control: FormControl): ValidationErrors | null {
        const reg = /(^[a-zA-Z0-9][a-zA-Z0-9-_.]*$)/;
        let isValid;
        if (control.value === '') {
            isValid = true;
        } else {
            isValid = reg.test(control.value);
        }
        return isValid ? null : { snapTag: true };
    }
}
